import { BaseGamePadBaseDrawer } from './BaseGamePadBaseDrawer';

export class BaseGamePadMobileDrawer extends BaseGamePadBaseDrawer {
  constructor() {
    super();
    this.sourceNames = {
      ...this.sourceNames,
      borderCircleActive: 'game_pad_mobile_drawer_border_circle_active',
      borderCircle: 'game_pad_mobile_drawer_border_circle',
      buttonsContent: 'game_pad_mobile_drawer_buttons_content',
      autoSpinPlayIcon: 'game_pad_mobile_drawer_auto_spin_play_icon',
      coinsIcon: 'game_pad_mobile_drawer_coins_icon',
    };
  }

  getCircleButton({
    icon, // required
    scale = 1,
    alpha = .7,
    size = 110,
    title = '',
    titleStyle = {
      fill: 0xFFFFFF,
      fontFamily: 'Lato',
      fontSize: 25,
      fontWeight: 900,
    },
  } = {}) {
    let flag = false;
    const radius = size * scale / 2;
    const container = this.createContainer(icon);
    const buttonHelperContainer = this.createContainer(`${icon}_buttonHelperContainer`);
    const activeCircle = this.getTexture({ name: this.sourceNames.borderCircleActive, scale });
    const circle = this.getTexture({ name: this.sourceNames.borderCircle, scale });
    const iconTexture = this.getTexture({ name: icon, scale });
    const titleTexture = this.createText(title, titleStyle);
    const circleAlpha = this.createGraphics()
      .beginFill(0x000000)
      .drawCircle(0, 0, radius - 2)
      .endFill();
    circleAlpha.alpha = alpha;
    activeCircle.alpha = 0;
    buttonHelperContainer.visible = false;
    titleTexture.scale.set(scale);
    titleTexture.anchor.set(.5);
    titleTexture.alpha = +flag;

    container.addChild(
      circleAlpha,
      circle,
      activeCircle,
      iconTexture,
      buttonHelperContainer,
      titleTexture,
    );

    container.size = size * scale;
    return {
      container,
      onToggle: flagState => {
        if (typeof flagState === 'boolean') {
          flag = flagState;
        } else {
          flag = !flag;
        }
        activeCircle.alpha = +flag;
        circle.alpha = +!flag;
        buttonHelperContainer.visible = flag;
        if (title) {
          titleTexture.alpha = +flag;
          iconTexture.visible = +!flag;
        }
      },
      buttonHelperContainer,
      * [Symbol.iterator]() {
        yield this.container;
        yield this.onToggle;
        yield this.buttonHelperContainer;
      },
    };
  }

  getButtonsContent({
    scale = 1,
    nextIcon,
    prevIcon,
    actionScale = 1,
    title = 'TITLE',
    titleStyle = {
      fill: 0xFFFFFF,
      fontSize: 26,
      align: 'center',
      fontWeight: 600,
    },
    valueStyle = {
      fill: 0xFFFFFF,
      fontSize: 26,
      align: 'center',
    },
    showCircle = true,
    circleColor = 0x636363,
    iconSize = 64, // content default width
    width = 254, // content default width
    height = 124, // content default width
    space = 13, // content default width
    onNext = (event, { content, button, circle }) => null,
    onPrev = (event, { content, button, circle }) => null,
  } = {}) {
    const textPadding = 4 * scale;
    const originalWidth = width * scale;
    const originalHeight = height * scale;
    const originalSpace = space * scale;
    const originalIconSize = iconSize * scale;
    const radius = originalIconSize / 2;
    const originalIconScale = actionScale * scale;

    const container = this.createContainer('buttonsContent');
    const valueContainer = this.createContainer('valueContainer');
    const buttonsContentTexture = this.getTexture({
      name: this.sourceNames.buttonsContent,
      scale,
    });
    const nextTexture = this.getTexture({
      name: nextIcon,
      scale: originalIconScale,
    });
    const prevTexture = this.getTexture({
      name: prevIcon,
      scale: originalIconScale,
    });
    const infinityIconTexture = this.getTexture({
      name: this.sourceNames.infinityIcon,
      scale: originalIconScale,
    });
    const nextCircle = this.createGraphics()
      .beginFill(circleColor)
      .drawCircle(
        0,
        0,
        radius,
      )
      .endFill();
    const prevCircle = nextCircle.clone();
    nextCircle.visible = showCircle;
    prevCircle.visible = showCircle;

    const titleTexture = this.createText(title, titleStyle);
    titleTexture.anchor.set(.5);
    titleTexture.scale.set(scale);
    this.setPosition(titleTexture, { y: -originalHeight / 2 + titleTexture.height / 2 + textPadding });

    const valueTexture = this.createText('', valueStyle);
    valueTexture.anchor.set(.5);
    valueTexture.scale.set(scale);

    const nextButtonContainer = this.createContainer('nextButtonContainer');
    nextButtonContainer.addChild(
      nextCircle,
      nextTexture,
    );
    const prevButtonContainer = this.createContainer('prevButtonContainer');
    prevButtonContainer.addChild(
      prevCircle,
      prevTexture,
    );
    const iconSpace = originalSpace + originalIconSize / 2;
    const iconY = originalHeight / 2 - iconSpace;
    this.setPosition(prevButtonContainer, { x: -originalWidth / 2 + iconSpace, y: iconY });
    this.setPosition(nextButtonContainer, { x: originalWidth / 2 - iconSpace, y: iconY });
    this.setPosition(valueContainer, { y: iconY });

    infinityIconTexture.visible = false;
    valueContainer.addChild(
      infinityIconTexture,
      valueTexture,
    );

    container.addChild(
      buttonsContentTexture,
      nextButtonContainer,
      prevButtonContainer,
      titleTexture,
      valueContainer,
    );

    this.setupButtonMode(nextButtonContainer);
    this.setupButtonMode(prevButtonContainer);

    this.registerClick(nextButtonContainer, event => {
      onNext(event, {
        content: nextButtonContainer,
        button: nextTexture,
        circle: nextCircle,
      });
    });
    this.registerClick(prevButtonContainer, event => {
      onPrev(event, {
        content: prevButtonContainer,
        button: prevTexture,
        circle: prevCircle,
      });
    });

    this.setupButtonMode(container);
    this.registerClick(container, event => event.stopPropagation());

    container.height = originalHeight;
    return {
      container,
      renderOption: value => {
        const isInfinity = value === Infinity;
        infinityIconTexture.visible = isInfinity;
        valueTexture.alpha = !+isInfinity;
        valueTexture.text = value;
      },
      elements: {
        prevCircle,
        nextCircle,
        prev: prevTexture,
        next: nextTexture,
      },
      * [Symbol.iterator]() {
        yield this.container;
        yield this.renderOption;
        yield this.elements;
      },
    };
  }
}
